import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import EMPInputField from "../../shared/components/EMPInputField/EMPInputField";
import { ModalContext } from "../../shared/components/EMPModal/ModalContext";
import { UserContext } from "../../shared/context/userContext";
import DealerOnboardingClient from "../../clients/DealerOnboardingClient";
import styles from "./InDealershipTeamSetup.module.scss";
import { DEALER_ONBOARDING_PROGRESS_QUERY_KEY } from "../../shared/utils/onboardingProgressUtil";

const SENTENCE_PERIOD = ".";

const teamMemberPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  roleTitle: PropTypes.string,
  email: PropTypes.string,
  phoneNumber: PropTypes.string,
  active: PropTypes.bool,
});

const getRoleTitleOptions = (t, selectedRoleTitle = "") => {
  const options = [
    {
      value: "Dealer/Partner",
      label: t(
        "DealerOnboarding.content.inDealershipTeamSetup.roleOptions.dealerPartner",
        "Dealer/Partner"
      ),
    },
    {
      value: "General Manager",
      label: t(
        "DealerOnboarding.content.inDealershipTeamSetup.roleOptions.generalManager",
        "General Manager"
      ),
    },
    {
      value: "General Sales Manager",
      label: t(
        "DealerOnboarding.content.inDealershipTeamSetup.roleOptions.generalSalesManager",
        "General Sales Manager"
      ),
    },
  ];

  if (
    selectedRoleTitle &&
    !options.some((option) => option.value === selectedRoleTitle)
  ) {
    options.unshift({
      value: selectedRoleTitle,
      label: selectedRoleTitle,
    });
  }

  return options;
};

const validateForm = ({ name, roleTitle, email, phoneNumber }, t) => {
  const errors = {};

  if (!name.trim()) {
    errors.name = t(
      "DealerOnboarding.content.inDealershipTeamSetup.validation.nameRequired",
      "Name is required"
    );
  } else if (!/^[A-Za-z\s]+$/.test(name.trim())) {
    errors.name = t(
      "DealerOnboarding.content.inDealershipTeamSetup.validation.nameInvalid",
      "Name can only include letters and spaces"
    );
  }

  if (!roleTitle) {
    errors.roleTitle = t(
      "DealerOnboarding.content.inDealershipTeamSetup.validation.roleTitleRequired",
      "Role Title is required"
    );
  }

  if (!email.trim()) {
    errors.email = t(
      "DealerOnboarding.content.inDealershipTeamSetup.validation.emailRequired",
      "Email is required"
    );
  } else if (!email.includes("@")) {
    errors.email = t(
      "DealerOnboarding.content.inDealershipTeamSetup.validation.emailInvalid",
      "Email must include @"
    );
  }

  if (!phoneNumber) {
    errors.phoneNumber = t(
      "DealerOnboarding.content.inDealershipTeamSetup.validation.phoneRequired",
      "Direct Phone or Cell is required"
    );
  } else if (phoneNumber.length !== 10) {
    errors.phoneNumber = t(
      "DealerOnboarding.content.inDealershipTeamSetup.validation.phoneInvalid",
      "Direct Phone or Cell must be 10 digits"
    );
  }

  return errors;
};

const AddTeamMemberModalContent = ({
  dealerCommonId,
  userId,
  onTeamMemberAdded,
  onTeamMemberUpdated = () => {},
  onTeamMemberDeleted = () => {},
  teamMember = null,
  /** Count of active team members on the roster (used to enforce at least one member when removing). */
  totalActiveTeamMembers = 0,
}) => {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const isEditMode = !!teamMember;

  const [formData, setFormData] = useState({
    name: teamMember?.name || "",
    roleTitle: teamMember?.roleTitle || "",
    email: teamMember?.email || "",
    phoneNumber: teamMember?.phoneNumber || "",
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [removeConfirmationOpen, setRemoveConfirmationOpen] = useState(false);

  const roleTitleOptions = getRoleTitleOptions(t, formData.roleTitle);

  const canRemoveTeamMember = isEditMode && totalActiveTeamMembers > 1;

  const addTeamMemberMutation = useMutation({
    mutationFn: (payload) =>
      DealerOnboardingClient.addTeamMember(dealerCommonId, payload, userId),
    onSuccess: async (createdTeamMember) => {
      await onTeamMemberAdded(createdTeamMember);
      closeModal();
    },
    onError: () => {
      setSubmitError(
        t(
          "DealerOnboarding.content.inDealershipTeamSetup.errors.unableToAdd",
          "Unable to add team member. Please try again."
        )
      );
    },
  });

  const updateTeamMemberMutation = useMutation({
    mutationFn: (payload) =>
      DealerOnboardingClient.updateTeamMember(
        dealerCommonId,
        teamMember.id,
        payload,
        userId
      ),
    onSuccess: (updatedTeamMember) => {
      onTeamMemberUpdated(updatedTeamMember);
      closeModal();
    },
    onError: () => {
      setSubmitError(
        t(
          "DealerOnboarding.content.inDealershipTeamSetup.errors.unableToUpdate",
          "Unable to update team member. Please try again."
        )
      );
    },
  });

  const deleteTeamMemberMutation = useMutation({
    mutationFn: () =>
      DealerOnboardingClient.deleteTeamMember(dealerCommonId, teamMember.id),
    onSuccess: () => {
      onTeamMemberDeleted();
      setRemoveConfirmationOpen(false);
      closeModal();
    },
    onError: () => {
      setSubmitError(
        t(
          "DealerOnboarding.content.inDealershipTeamSetup.errors.unableToDelete",
          "Unable to delete team member. Please try again."
        )
      );
    },
  });

  const isSubmitting =
    addTeamMemberMutation.isPending ||
    updateTeamMemberMutation.isPending ||
    deleteTeamMemberMutation.isPending;

  const handleNameChange = (event) => {
    const sanitizedValue = event.target.value.replace(/[^A-Za-z\s]/g, "");
    setFormData((prev) => ({
      ...prev,
      name: sanitizedValue,
    }));
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: "" }));
    }
  };

  const handleRoleTitleChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      roleTitle: event.target.value,
    }));
    if (errors.roleTitle) {
      setErrors((prev) => ({ ...prev, roleTitle: "" }));
    }
  };

  const handleEmailChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      email: event.target.value,
    }));
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  const handlePhoneNumberChange = (event) => {
    const sanitizedValue = event.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({
      ...prev,
      phoneNumber: sanitizedValue,
    }));
    if (errors.phoneNumber) {
      setErrors((prev) => ({ ...prev, phoneNumber: "" }));
    }
  };

  const handleSubmit = () => {
    const validationErrors = validateForm(formData, t);
    setErrors(validationErrors);
    setSubmitError("");

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    if (!dealerCommonId) {
      setSubmitError(
        t(
          "DealerOnboarding.content.inDealershipTeamSetup.errors.unableToAddNow",
          "Unable to add team member right now. Please try again."
        )
      );
      return;
    }

    const payload = {
      name: formData.name.trim(),
      roleTitle: formData.roleTitle,
      email: formData.email.trim(),
      phoneNumber: formData.phoneNumber,
    };

    if (isEditMode) {
      updateTeamMemberMutation.mutate(payload);
      return;
    }

    addTeamMemberMutation.mutate(payload);
  };

  const handleOpenRemoveConfirmation = () => {
    setSubmitError("");
    if (!canRemoveTeamMember) {
      return;
    }
    setRemoveConfirmationOpen(true);
  };

  const handleCloseRemoveConfirmation = () => {
    setSubmitError("");
    setRemoveConfirmationOpen(false);
  };

  const handleDelete = () => {
    setSubmitError("");

    if (!canRemoveTeamMember) {
      return;
    }

    if (!dealerCommonId || !teamMember?.id) {
      setSubmitError(
        t(
          "DealerOnboarding.content.inDealershipTeamSetup.errors.unableToDeleteNow",
          "Unable to delete team member right now. Please try again."
        )
      );
      return;
    }

    deleteTeamMemberMutation.mutate();
  };

  return (
    <Box className={styles.modalContent}>
      <Typography variant="body1" className={styles.modalDescription}>
        {isEditMode ? (
          <>
            {t(
              "DealerOnboarding.content.inDealershipTeamSetup.modal.editDescriptionPrefix",
              "To edit this team member, update the necessary information then select"
            )}
            {" "}
            <strong>
              {t(
                "DealerOnboarding.content.inDealershipTeamSetup.updateTeamMember",
                "Update"
              )}
            </strong>
            {" "}
            {t(
              "DealerOnboarding.content.inDealershipTeamSetup.modal.editDescriptionMiddle",
              "to save. To remove them, select"
            )}
            {" "}
            <strong>
              {t(
                "DealerOnboarding.content.inDealershipTeamSetup.modal.editDescriptionRemoveCta",
                "Remove Team Member"
              )}
            </strong>
            {SENTENCE_PERIOD}
          </>
        ) : (
          <>
            {t(
              "DealerOnboarding.content.inDealershipTeamSetup.modal.descriptionPrefix",
              "Provide team member's information and select"
            )}
            {" "}
            <strong>
              {t(
                "DealerOnboarding.content.inDealershipTeamSetup.addTeamMember",
                "Add team member"
              )}
            </strong>
            {SENTENCE_PERIOD}
          </>
        )}
      </Typography>

      <Box className={styles.modalForm}>
        <Box mb={2}>
          <InputLabel disableAnimation shrink={false}>
            {t(
              "DealerOnboarding.content.inDealershipTeamSetup.fields.name",
              "Name"
            )}
          </InputLabel>
          <EMPInputField
            testId="team-member-name-input"
            value={formData.name}
            onChange={handleNameChange}
            error={!!errors.name}
            helperText={errors.name}
          />
        </Box>

        <Box mb={2}>
          <TextField
            select
            fullWidth
            size="small"
            variant="standard"
            label={t(
              "DealerOnboarding.content.inDealershipTeamSetup.fields.roleTitle",
              "Role Title"
            )}
            value={formData.roleTitle}
            onChange={handleRoleTitleChange}
            error={!!errors.roleTitle}
            helperText={errors.roleTitle}
            SelectProps={{ native: true }}
          >
            <option value="" />
            {roleTitleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TextField>
        </Box>

        <Box mb={2}>
          <InputLabel disableAnimation shrink={false}>
            {t(
              "DealerOnboarding.content.inDealershipTeamSetup.fields.email",
              "Email"
            )}
          </InputLabel>
          <EMPInputField
            testId="team-member-email-input"
            value={formData.email}
            onChange={handleEmailChange}
            error={!!errors.email}
            helperText={errors.email}
          />
        </Box>

        <Box mb={2}>
          <InputLabel disableAnimation shrink={false}>
            {t(
              "DealerOnboarding.content.inDealershipTeamSetup.fields.directPhoneOrCell",
              "Direct Phone or Cell"
            )}
          </InputLabel>
          <EMPInputField
            testId="team-member-phone-input"
            value={formData.phoneNumber}
            onChange={handlePhoneNumberChange}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber}
          />
        </Box>

        {submitError && !removeConfirmationOpen && (
          <Typography variant="body2" className={styles.modalError}>
            {submitError}
          </Typography>
        )}
      </Box>

      <Box
        className={`${styles.modalActions} ${
          isEditMode ? styles.editModalActions : ""
        }`}
      >
        <EMPButton
          variant="outlined"
          onClick={closeModal}
          disabled={isSubmitting}
          testId="cancel-add-team-member-button"
          className={styles.cancelButton}
        >
          {t(
            "DealerOnboarding.content.inDealershipTeamSetup.cancel",
            "Cancel"
          )}
        </EMPButton>
        {isEditMode ? (
          <>
            <EMPButton
              variant="outlined"
              onClick={handleOpenRemoveConfirmation}
              disabled={isSubmitting || !canRemoveTeamMember}
              testId="open-remove-team-member-confirmation-button"
              className={`${styles.cancelButton} ${styles.removeButton}`}
              title={
                canRemoveTeamMember
                  ? undefined
                  : t(
                      "DealerOnboarding.content.inDealershipTeamSetup.removeTeamMemberDisabledHint",
                      "At least one team member is required. Add another member before removing this one."
                    )
              }
            >
              {t(
                "DealerOnboarding.content.inDealershipTeamSetup.removeTeamMember",
                "Remove team member"
              )}
            </EMPButton>
            <EMPButton
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              testId="confirm-update-team-member-button"
              className={styles.addButton}
            >
              {isSubmitting
                ? t(
                    "DealerOnboarding.content.inDealershipTeamSetup.updating",
                    "Updating..."
                  )
                : t(
                    "DealerOnboarding.content.inDealershipTeamSetup.updateTeamMember",
                    "Update"
                  )}
            </EMPButton>
            {!canRemoveTeamMember && (
              <Typography
                variant="caption"
                component="p"
                className={styles.minimumMembersHint}
                data-testid="minimum-team-members-hint"
              >
                {t(
                  "DealerOnboarding.content.inDealershipTeamSetup.minimumOneMemberRequired",
                  "At least one team member must remain. Add another team member before you can remove this one."
                )}
              </Typography>
            )}
          </>
        ) : (
          <EMPButton
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            testId="confirm-add-team-member-button"
            className={styles.addButton}
          >
            {isSubmitting
              ? t(
                  "DealerOnboarding.content.inDealershipTeamSetup.adding",
                  "Adding..."
                )
              : t(
                  "DealerOnboarding.content.inDealershipTeamSetup.addTeamMember",
                  "Add team member"
                )}
          </EMPButton>
        )}
      </Box>

      <Dialog
        open={removeConfirmationOpen}
        onClose={handleCloseRemoveConfirmation}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: styles.removeConfirmationDialog }}
      >
        <DialogTitle>
          {t(
            "DealerOnboarding.content.inDealershipTeamSetup.modal.removeConfirmationTitle",
            {
              defaultValue: "Remove team member {{name}}",
              name: teamMember?.name || "",
            }
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t(
              "DealerOnboarding.content.inDealershipTeamSetup.modal.removeConfirmationQuestion",
              {
                defaultValue:
                  "Are you sure you want to remove {{name}} from your KDMs?",
                name: teamMember?.name || "",
              }
            )}
            <br />
            {t(
              "DealerOnboarding.content.inDealershipTeamSetup.modal.removeConfirmationBody",
              "This action cannot be undone."
            )}
          </Typography>
          {submitError && removeConfirmationOpen && (
            <Typography variant="body2" className={styles.modalError}>
              {submitError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <EMPButton
            variant="outlined"
            onClick={handleCloseRemoveConfirmation}
            disabled={isSubmitting}
            testId="go-back-remove-team-member-button"
            className={styles.cancelButton}
          >
            {t(
              "DealerOnboarding.content.inDealershipTeamSetup.goBack",
              "Go Back"
            )}
          </EMPButton>
          <EMPButton
            variant="contained"
            onClick={handleDelete}
            disabled={isSubmitting}
            testId="confirm-remove-team-member-button"
            className={styles.addButton}
          >
            {isSubmitting
              ? t(
                  "DealerOnboarding.content.inDealershipTeamSetup.removing",
                  "Removing..."
                )
              : t(
                  "DealerOnboarding.content.inDealershipTeamSetup.remove",
                  "Remove"
                )}
          </EMPButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

AddTeamMemberModalContent.propTypes = {
  dealerCommonId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onTeamMemberAdded: PropTypes.func,
  onTeamMemberUpdated: PropTypes.func,
  onTeamMemberDeleted: PropTypes.func,
  teamMember: teamMemberPropType,
  totalActiveTeamMembers: PropTypes.number,
};

const InDealershipTeamSetup = ({
  onAddTeamMember = () => {},
  testId = "in-dealership-team-setup",
}) => {
  const { t } = useTranslation();
  const { handleModal } = useContext(ModalContext);
  const { user } = useContext(UserContext);
  const queryClient = useQueryClient();

  const dealerCommonId =
    user?.dealerCommonId || user?.commonId || user?.commonDealerId;
  const userId = user?.userId || user?.commonName;

  const { data: teamMembers = [] } = useQuery({
    queryKey: ["teamMembers", dealerCommonId],
    queryFn: () => DealerOnboardingClient.getTeamMembers(dealerCommonId),
    enabled: !!dealerCommonId,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const activeTeamMembers = teamMembers.filter((member) => member.active);

  const handleOpenAddTeamMemberModal = () => {
    onAddTeamMember();
    handleModal(
      t(
        "DealerOnboarding.content.inDealershipTeamSetup.modal.title",
        "Add new team member"
      ),
      <AddTeamMemberModalContent
        dealerCommonId={dealerCommonId}
        userId={userId}
        onTeamMemberAdded={() => {
          queryClient.invalidateQueries({
            queryKey: ["teamMembers", dealerCommonId],
          });
          queryClient.invalidateQueries({
            queryKey: [DEALER_ONBOARDING_PROGRESS_QUERY_KEY, dealerCommonId],
          });
        }}
      />
    );
  };

  const handleOpenDeleteTeamMemberModal = (teamMember) => {
    handleModal(
      t("DealerOnboarding.content.inDealershipTeamSetup.modal.editTitle", {
        defaultValue: "Edit or remove {{name}}",
        name: teamMember?.name || "",
      }),
      <AddTeamMemberModalContent
        dealerCommonId={dealerCommonId}
        userId={userId}
        teamMember={teamMember}
        totalActiveTeamMembers={activeTeamMembers.length}
        onTeamMemberAdded={() =>
          queryClient.invalidateQueries({
            queryKey: ["teamMembers", dealerCommonId],
          })
        }
        onTeamMemberUpdated={() =>
          queryClient.invalidateQueries({
            queryKey: ["teamMembers", dealerCommonId],
          })
        }
        onTeamMemberDeleted={() =>
          queryClient.invalidateQueries({
            queryKey: ["teamMembers", dealerCommonId],
          })
        }
      />
    );
  };

  return (
    <Box className={styles.container} data-testid={testId}>
      <Box className={styles.introduction}>
        <Typography variant="body1" className={styles.introductionText}>
          {t(
            "DealerOnboarding.content.inDealershipTeamSetup.description",
            "Select the person or people within your dealership that will receive email alerts for eligible leads and purchase requests."
          )}
        </Typography>
      </Box>

      <Box className={styles.cardsSection}>
        <Box className={styles.teamMemberList}>
          {activeTeamMembers.map((member) => (
            <Box
              key={member.id}
              className={styles.teamMemberCard}
              onClick={() => handleOpenDeleteTeamMemberModal(member)}
              style={{ cursor: "pointer" }}
            >
              <Typography variant="h6" className={styles.teamMemberName}>
                {member.name}
              </Typography>

              <Typography variant="body2" className={styles.teamMemberRole}>
                {member.roleTitle}, {member.email}
              </Typography>

              <Typography variant="body2" className={styles.teamMemberPhone}>
                {member.phoneNumber}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box className={styles.buttonContainer}>
          <EMPButton
            variant="contained"
            onClick={handleOpenAddTeamMemberModal}
            testId="add-team-member-button"
            className={styles.addButton}
          >
            {t(
              "DealerOnboarding.content.inDealershipTeamSetup.addTeamMember",
              "Add team member"
            )}
          </EMPButton>
        </Box>
      </Box>
    </Box>
  );
};

InDealershipTeamSetup.propTypes = {
  onAddTeamMember: PropTypes.func,
  testId: PropTypes.string,
};

export default InDealershipTeamSetup;